// ─── All pure math helpers remain unchanged ───────────────────────────────────
// Old-portal PHP's numberformatperformancetracker() always calls round($value)
// — spend/revenue/percentage figures are whole numbers there. ROAS values
// alone go through a separate number_format($value, 2) path (see calcRoas).
export const calcVariance = (actual, goal) => goal > 0 ? Math.round(((actual - goal) / goal) * 100) : 0;
// Mirrors PHP's number_format($value, 2, '.', '') used specifically for ROAS.
export const calcRoas = (revenue, spend) => spend !== 0 ? Number((revenue / spend).toFixed(2)) : 0;
export const safeDiv = (numerator, denominator) => denominator !== 0 ? numerator / denominator : 0;
// ─── Modifier Helpers (unchanged) ────────────────────────────────────────────
export const calculateEffectiveModifiers = (allRows, modifiers) => {
    // Reverted per product decision: the new portal intentionally shows LIVE
    // auto-distribution (a manual modifier on one day automatically spreads
    // an offsetting % across every other day so the month nets to zero) —
    // this is a deliberate divergence from the old-portal PHP behavior,
    // which only ever applies a modifier to its own exact date. Keep in
    // sync with the backend's calculateEffectiveModifiersForDates in
    // accountPerformanceTrackerHelper.ts.
    const effectiveMods = {};
    const anchorDates = [];
    allRows.forEach(row => {
        // All manually set modifiers are anchors (including 0).
        // If a date is in the modifiers object, it was manually saved by the user.
        if (modifiers[row.date] !== undefined) {
            anchorDates.push(row.date);
            effectiveMods[row.date] = Number(modifiers[row.date]);
        }
    });
    if (anchorDates.length === 0)
        return effectiveMods;
    const totalEnteredModifier = anchorDates.reduce((sum, date) => sum + Number(modifiers[date] || 0), 0);
    // Total non-anchor days in the month (past or future)
    const nonAnchorRows = allRows.filter(row => modifiers[row.date] === undefined);
    if (nonAnchorRows.length > 0) {
        // Rounded to 2dp so the value used in every downstream calculation
        // (Original Daily, future-day projections, etc.) matches the number
        // shown in the modifier input box — not a longer-precision float.
        const autoAdjustment = Number((-(totalEnteredModifier / nonAnchorRows.length)).toFixed(2));
        // Distribute the offset across every non-anchor day in the month
        // so the entered modifiers always net to zero over the remaining days.
        nonAnchorRows.forEach(row => {
            effectiveMods[row.date] = autoAdjustment;
        });
    }
    return effectiveMods;
};
export const applyModifierToGoal = (originalDaily, mod) => originalDaily * (1 + mod / 100);
// ─── Output rounding (final display values only — not applied mid-chain) ─────
// Matches the old-portal PHP split: numberformatperformancetracker() rounds
// spend/revenue/percentage figures to whole numbers; number_format($v, 2)
// keeps ROAS values at 2 decimals.
const roundWhole = (v) => Math.round(v);
const roundRoas = (v) => Number(v.toFixed(2));
const INITIAL_BUDGET_CATCHUP_STATE = { spendDiff: 0, orgDailyModifierMtd: 0 };
const advanceBudgetCatchUpState = (params) => {
    const { flatDailySpend, mod, dayCount, daysInMonth, spendToday, mtdSpendBeforeDay, prevState } = params;
    const orgDailyModifier = flatDailySpend * (1 + mod / 100);
    const orgDailyModifierMtd = dayCount === 1
        ? orgDailyModifier
        : orgDailyModifier + prevState.orgDailyModifierMtd;
    const actReprojMtd = mtdSpendBeforeDay + spendToday;
    const remainingDaysAfterToday = daysInMonth - dayCount;
    const spendDiff = remainingDaysAfterToday !== 0
        ? (orgDailyModifierMtd - actReprojMtd) / remainingDaysAfterToday
        : 0;
    return { spendDiff, orgDailyModifierMtd };
};
// ─── Spend Goal Daily (consumes formulas from API) ────────────────────────────
export const calcSpendGoalDaily = (params) => {
    const { isBudgetBased, revGoalDaily, roasGoal, spendGoal, spendMtd, remainingDays, } = params;
    if (isBudgetBased) {
        return remainingDays > 0
            ? safeDiv(spendGoal - spendMtd, remainingDays)
            : 0;
    }
    return roasGoal > 0 ? revGoalDaily / roasGoal : 0;
};
export const calcFutureSpendDaily = (params) => {
    const { isRoasBased, isBudgetBased, totalProjectedRevenue, originalMtdRoas, mtdSpendBeforeDay, actualDailyRevenue, lastActualMtdRevenue, currentMtdRevenue, roasDownPer, roasUpPer, lastActualDailySpend, spendGoal, daysInMonth, dayCount, mod = 0, prevSpendDiff = 0, } = params;
    if (isRoasBased && originalMtdRoas > 0) {
        const spendDaily1 = totalProjectedRevenue / originalMtdRoas - mtdSpendBeforeDay;
        const denominator = totalProjectedRevenue - lastActualMtdRevenue;
        const spendDaily2 = denominator !== 0 ? actualDailyRevenue / denominator : 0;
        const newDailySpend = spendDaily1 * spendDaily2;
        // Day 1 of the month is a hard-coded special case in the old-portal
        // PHP (`$i==0`), keyed off the calendar date — not off whether any
        // spend has happened yet. A day 2+ with $0 prior spend must still go
        // through the normal ratio/override logic below.
        if (dayCount === 1) {
            return newDailySpend;
        }
        // ✅ If newDailySpend is negative (meaning we have exceeded the ROAS implied budget),
        if (newDailySpend < 0) {
            return newDailySpend;
        }
        // PHP leaves roas_act_reproj_mtd at 0 when spend_act_reproj_mtd is 0
        // (never divides by zero), which reads as a full -100% variance —
        // not "no variance". Mirror that instead of producing NaN/Infinity.
        const currentRoasMtd = mtdSpendBeforeDay > 0
            ? lastActualMtdRevenue / mtdSpendBeforeDay
            : 0;
        const roasVariance = originalMtdRoas > 0
            ? ((currentRoasMtd - originalMtdRoas) / originalMtdRoas) * 100
            : 0;
        const spendGoalDaily = (spendGoal / daysInMonth) * (1 + mod / 100);
        const spendGoalMtdBeforeDay = spendGoalDaily * (dayCount - 1);
        const spendVariance = spendGoalMtdBeforeDay > 0
            ? ((mtdSpendBeforeDay - spendGoalMtdBeforeDay) / spendGoalMtdBeforeDay) * 100
            : 0;
        if (Math.round(roasVariance) > 0 && roasUpPer != 0) {
            return newDailySpend * (1 + roasUpPer / 100);
        }
        else if (Math.round(roasVariance) < 0 && roasDownPer != 0) {
            // Day 6+ down-adjustment: signed delta between today's ratio-based
            // spend and yesterday's actual spend, scaled by roasDownPer — NOT
            // an abs()-based pull-down, and NOT the previous (lastActual - new)
            // reversed-sign version either (that was a pre-existing frontend/
            // backend inconsistency). Matches the reference spreadsheet's
            // G + (G - H_prev) x D48. Days 2-5 are unaffected: the caller's
            // override always replaces this branch's result for that window.
            // The reference spreadsheet stores its down-modifier (D48) as a
            // NEGATIVE fraction (e.g. -0.5 = -50%), which is what makes
            // G + (G-H_prev)*D48 average toward yesterday's spend. The live
            // database stores the same setting as a POSITIVE percentage
            // (verified: paid_roas_down_adjust_per = 50, not -50) — negate it
            // here so the live value reproduces the spreadsheet's intended
            // sign/behavior regardless of how it's stored.
            const spendDiff = (newDailySpend - lastActualDailySpend) * (-roasDownPer / 100);
            return newDailySpend + spendDiff;
        }
        else {
            return newDailySpend;
        }
    }
    // ── Budget-Based ──────────────────────────────────────────────────────────
    // Old-portal PHP: spend_org_proj_daily = flatDaily + yesterday's spenddiff
    // (0 on day 1), then spend_act_reproj_daily = that value boosted by
    // TODAY's own modifier — not the plain remaining-budget/remaining-days
    // result scaled by (1+mod/100) once a modifier has been in play earlier.
    if (isBudgetBased) {
        const flatDailySpend = daysInMonth > 0 ? spendGoal / daysInMonth : 0;
        const orgProjDaily = flatDailySpend + (dayCount === 1 ? 0 : prevSpendDiff);
        return orgProjDaily * (1 + mod / 100);
    }
    return 0;
};
// ─── Main Row Builder (now accepts formulas from API) ─────────────────────────
export const buildTrackerRows = (tableData, metrics, modifiers, isSmartProjection, formulas, // ✅ Consumed from API response
savedProjections = {} // ✅ NEW
) => {
    if (!tableData?.length)
        return [];
    // ✅ All constants come from formulas — no re-derivation here
    const { isRoasBased, isBudgetBased, roasGoal, projectedDailyRevenue, totalSpendGoal, totalSitewideSpendGoal, originalDailySitewideSpend, totalProjectedRevenue, projectedDailySitewideRevenue, roasDownPer, roasUpPer, daysInMonth, metaSpendPercent, adwordSpendPercent, bingSpendPercent, criteoSpendPercent, } = formulas;
    // ✅ Site Wide Access: when on, every goal-driven figure (goal columns,
    const sitewideRoasGoal = Number(metrics?.paid_site_roas_final || 0);
    const effectiveRoasGoal = isSmartProjection && sitewideRoasGoal > 0 ? sitewideRoasGoal : roasGoal;
    const rawRevGoal = isSmartProjection
        ? (formulas.totalSitewideRevenue > 0 ? formulas.totalSitewideRevenue : totalProjectedRevenue)
        : (totalProjectedRevenue > 0 ? totalProjectedRevenue : projectedDailyRevenue * daysInMonth);
    const effectiveSpendGoal = isRoasBased
        ? (effectiveRoasGoal > 0 ? rawRevGoal / effectiveRoasGoal : totalSpendGoal)
        : (isSmartProjection && totalSitewideSpendGoal > 0 ? totalSitewideSpendGoal : totalSpendGoal);
    const effectiveOriginalDailySpend = daysInMonth > 0 ? effectiveSpendGoal / daysInMonth : 0;
    const getOriginalDailyRevenue = (row) => isSmartProjection ? projectedDailySitewideRevenue : (row.revenue?.originalDaily || projectedDailyRevenue || 0);
    const getOriginalDailySpend = (row) => effectiveOriginalDailySpend;
    const getRevGoalDaily = (row, modPct) => {
        const base = getOriginalDailyRevenue(row);
        return applyModifierToGoal(base, modPct);
    };
    const effectiveMods = calculateEffectiveModifiers(tableData, modifiers);
    // ── Pre-pass: compute fixed totalMonthlyRevenue (= Total row's rev_mtd) ──
    // Step 1: get frozenActualMtd and frozenRevGoalMtd from actual days
    let preFrozenActualMtd = 0;
    let preFrozenRevGoalMtd = 0;
    tableData.forEach((row) => {
        if (row.isFuture === true)
            return;
        const rawMod = effectiveMods[row.date] !== undefined ? effectiveMods[row.date] : 0;
        const mod = isNaN(rawMod) ? 0 : rawMod;
        const revGoalDaily = getRevGoalDaily(row, mod);
        const actualRev = isSmartProjection && row.advanced_revenue ? row.advanced_revenue : row.revenue;
        preFrozenActualMtd += actualRev?.daily || 0;
        preFrozenRevGoalMtd += revGoalDaily;
    });
    const prePerformanceRatio = preFrozenRevGoalMtd > 0 ? preFrozenActualMtd / preFrozenRevGoalMtd : 1;
    // Step 2: sum actual + all future projected = totalMonthlyRevenue
    let totalMonthlyRevenue = preFrozenActualMtd;
    tableData.forEach((row) => {
        if (row.isFuture !== true)
            return;
        const rawMod = effectiveMods[row.date] !== undefined ? effectiveMods[row.date] : 0;
        const mod = isNaN(rawMod) ? 0 : rawMod;
        const revGoalDaily = getRevGoalDaily(row, mod);
        totalMonthlyRevenue += revGoalDaily * prePerformanceRatio;
    });
    // ─────────────────────────────────────────────────────────────────────────
    let runningRevGoalMtd = 0;
    let runningRevActualMtd = 0;
    let runningSpendGoalMtd = 0;
    let runningSpendMtd = 0;
    let frozenActualMtdRevenue = 0;
    let lastActualDailySpend = 0;
    let hasActualData = false;
    let lastGoalMtd = 0;
    let yesterdayRoasVar = 0;
    let prevActualRevMtd = 0;
    let prevActualSpendMtd = 0;
    let budgetCatchUpState = INITIAL_BUDGET_CATCHUP_STATE;
    const rows = [];
    tableData.forEach((row, dayIndex) => {
        const isFuture = row.isFuture === true;
        const dayCount = dayIndex + 1;
        const rawMod = effectiveMods[row.date] !== undefined
            ? effectiveMods[row.date]
            : 0;
        const mod = isNaN(rawMod) ? 0 : rawMod;
        const appliedMod = mod;
        const revGoalDaily = getRevGoalDaily(row, appliedMod);
        const remainingDays = daysInMonth - dayIndex;
        const spendGoalDaily = calcSpendGoalDaily({
            isBudgetBased,
            revGoalDaily,
            roasGoal: effectiveRoasGoal,
            spendGoal: effectiveSpendGoal,
            spendMtd: runningSpendMtd,
            remainingDays,
        });
        const modifiedSpendGoalDaily = applyModifierToGoal(getOriginalDailySpend(row), appliedMod);
        // ✅ ROAS-based clients: the "Original Daily/MTD" spend goal must be
        const finalSpendGoalDaily = isRoasBased ? spendGoalDaily : modifiedSpendGoalDaily;
        runningRevGoalMtd += revGoalDaily;
        runningSpendGoalMtd += finalSpendGoalDaily;
        // ✅ "Predicted" spend for THIS day — the same performance-ratio + ROAS
        const mtdSpendBeforeToday = runningSpendMtd;
        const mtdRevBeforeToday = runningRevActualMtd;
        const prevGoalMtd = runningRevGoalMtd - revGoalDaily;
        const ratioRevMtd = mtdRevBeforeToday;
        const ratioGoalMtd = prevGoalMtd;
        const rawRatio = hasActualData && ratioGoalMtd > 0
            ? ratioRevMtd / ratioGoalMtd
            : 1;
        const performanceRatio = rawRatio;
        const predictedRevDaily = performanceRatio * revGoalDaily;
        let predictedSpendDaily = calcFutureSpendDaily({
            isRoasBased,
            isBudgetBased,
            totalProjectedRevenue: totalMonthlyRevenue,
            originalMtdRoas: effectiveRoasGoal,
            mtdSpendBeforeDay: mtdSpendBeforeToday,
            actualDailyRevenue: predictedRevDaily,
            lastActualMtdRevenue: mtdRevBeforeToday,
            currentMtdRevenue: mtdRevBeforeToday,
            roasDownPer,
            roasUpPer,
            lastActualDailySpend,
            spendGoal: effectiveSpendGoal,
            daysInMonth,
            dayCount,
            mod: appliedMod,
            prevSpendDiff: budgetCatchUpState.spendDiff,
        });
        // Days 2-5 override — old-portal PHP only special-cases this window
        // for ROAS-Based clients (no equivalent for Budget-Based), and resets
        // to the plain goal-implied spend whenever the previous day's ROAS
        // wasn't strictly ahead of goal (not only when it was negative) —
        // including when roasUpPer is configured as 0.
        if (isRoasBased && dayCount >= 2 && dayCount <= 5 && mtdSpendBeforeToday > 0) {
            const shouldBoost = Math.round(yesterdayRoasVar) > 0 && roasUpPer !== 0;
            if (!shouldBoost) {
                predictedSpendDaily = finalSpendGoalDaily > 0
                    ? finalSpendGoalDaily
                    : (effectiveRoasGoal > 0 ? revGoalDaily / effectiveRoasGoal : 0);
            }
        }
        const predictedSpendMeta = predictedSpendDaily * metaSpendPercent / 100;
        const predictedSpendSearch = predictedSpendDaily * adwordSpendPercent / 100;
        const predictedSpendBing = predictedSpendDaily * bingSpendPercent / 100;
        const predictedSpendCriteo = predictedSpendDaily * criteoSpendPercent / 100;
        let revDaily;
        let spendDaily;
        let spendMeta;
        let spendSearch;
        let spendBing;
        let spendCriteo;
        if (!isFuture) {
            const actualRev = isSmartProjection && row.advanced_revenue
                ? row.advanced_revenue
                : row.revenue;
            const backendRevDaily = actualRev?.daily || 0;
            revDaily = backendRevDaily;
            spendDaily = row.spend?.daily || 0;
            spendMeta = row.spend?.meta || 0;
            spendSearch = row.spend?.search || 0;
            spendBing = row.spend?.bing || 0;
            spendCriteo = row.spend?.criteo || 0;
            runningRevActualMtd += revDaily;
            runningSpendMtd += spendDaily;
            frozenActualMtdRevenue = runningRevActualMtd;
            hasActualData = true;
            lastGoalMtd = runningRevGoalMtd;
        }
        else {
            // Future day - always dynamic calculation
            revDaily = predictedRevDaily;
            spendDaily = predictedSpendDaily;
            spendMeta = spendDaily * metaSpendPercent / 100;
            spendSearch = spendDaily * adwordSpendPercent / 100;
            spendBing = spendDaily * bingSpendPercent / 100;
            spendCriteo = spendDaily * criteoSpendPercent / 100;
            runningRevActualMtd += revDaily;
            runningSpendMtd += spendDaily;
        }
        // ✅ FIX #1: Update lastActualDailySpend for BOTH actual and future days
        lastActualDailySpend = spendDaily;
        // Advance the Budget-Based catch-up state every day (actual and
        // future alike) — PHP's spenddiff bookkeeping runs continuously
        // through the whole month, not just on projected days.
        if (isBudgetBased) {
            budgetCatchUpState = advanceBudgetCatchUpState({
                flatDailySpend: effectiveOriginalDailySpend,
                mod: appliedMod,
                dayCount,
                daysInMonth,
                spendToday: spendDaily,
                mtdSpendBeforeDay: mtdSpendBeforeToday,
                prevState: budgetCatchUpState,
            });
        }
        const spendMtd = runningSpendMtd;
        const revMtd = runningRevActualMtd;
        const effectivePredictedSpendDaily = (!isFuture && mtdSpendBeforeToday <= 0) ? 0 : predictedSpendDaily;
        const predictedRevMtd = prevActualRevMtd + predictedRevDaily;
        const predictedSpendMtd = prevActualSpendMtd + effectivePredictedSpendDaily;
        if (!isFuture) {
            prevActualRevMtd = revMtd;
            prevActualSpendMtd = spendMtd;
        }
        const revVar = calcVariance(revMtd, runningRevGoalMtd);
        const spendVar = calcVariance(spendMtd, runningSpendGoalMtd);
        const roasDaily = calcRoas(revDaily, spendDaily);
        const roasMtd = calcRoas(revMtd, spendMtd);
        // ✅ Always self-computed: row.roas.* from the API is never modifier-
        // or Site-Wide-Access-aware, so falling back to it (as before) silently
        // ignored both the modifier and the sitewide ROAS goal.
        const roasGoalMtd = isSmartProjection ? effectiveRoasGoal : (row.roas?.originalMtd || roasGoal);
        const roasVar = calcVariance(roasMtd, roasGoalMtd);
        yesterdayRoasVar = roasVar;
        const isAnchor = modifiers[row.date] !== undefined;
        const hasEffectiveMod = !isAnchor && effectiveMods[row.date] !== undefined;
        rows.push({
            ...row,
            id: row.date,
            spend_meta: roundWhole(spendMeta),
            spend_search: roundWhole(spendSearch),
            spend_bing: roundWhole(spendBing),
            spend_criteo: roundWhole(spendCriteo),
            spend_daily: roundWhole(spendDaily),
            spend_mtd: roundWhole(spendMtd),
            spend_goal_daily: roundWhole(finalSpendGoalDaily),
            spend_goal_mtd: roundWhole(runningSpendGoalMtd),
            spend_var: spendVar,
            spend_meta_goal: roundWhole(finalSpendGoalDaily * metaSpendPercent / 100),
            spend_search_goal: roundWhole(finalSpendGoalDaily * adwordSpendPercent / 100),
            spend_bing_goal: roundWhole(finalSpendGoalDaily * bingSpendPercent / 100),
            spend_criteo_goal: roundWhole(finalSpendGoalDaily * criteoSpendPercent / 100),
            spend_daily_predicted: roundWhole(predictedSpendDaily),
            spend_mtd_predicted: roundWhole(predictedSpendMtd),
            spend_meta_predicted: roundWhole(predictedSpendMeta),
            spend_search_predicted: roundWhole(predictedSpendSearch),
            spend_bing_predicted: roundWhole(predictedSpendBing),
            spend_criteo_predicted: roundWhole(predictedSpendCriteo),
            rev_daily: roundWhole(revDaily),
            rev_daily_predicted: roundWhole(predictedRevDaily),
            rev_mtd: roundWhole(revMtd),
            rev_mtd_predicted: roundWhole(predictedRevMtd),
            rev_modifier: isAnchor ? modifiers[row.date] : null,
            rev_modifier_effective: mod,
            rev_modifier_has_effective: hasEffectiveMod,
            rev_goal_daily: roundWhole(revGoalDaily),
            rev_goal_mtd: roundWhole(runningRevGoalMtd),
            rev_var: revVar,
            roas_daily: roasDaily,
            roas_mtd: roasMtd,
            roas_goal_mtd: roundRoas(roasGoalMtd),
            roas_var: roasVar,
            spend_traffic_meta: row.traffic?.meta || 0,
            spend_traffic_search: row.traffic?.search || 0,
            spend_traffic_total: row.traffic?.total || 0,
        });
    });
    return rows;
};
// ─── Total Builder (unchanged) ────────────────────────────────────────────────
export const buildTrackerTotal = (rows, originalTotal, metrics) => {
    if (!rows.length || !originalTotal)
        return originalTotal;
    const roasGoal = Number(metrics.roas_goal || 1);
    const lastRow = rows[rows.length - 1];
    const totalSpendMtd = lastRow.spend_mtd;
    const totalRevMtd = lastRow.rev_mtd;
    const totalSpendGoalMtd = lastRow.spend_goal_mtd;
    const totalRevGoalMtd = lastRow.rev_goal_mtd;
    const totalMetaSpend = rows.reduce((s, r) => s + (r.spend_meta || 0), 0);
    const totalSearchSpend = rows.reduce((s, r) => s + (r.spend_search || 0), 0);
    const totalBingSpend = rows.reduce((s, r) => s + (r.spend_bing || 0), 0);
    const totalCriteoSpend = rows.reduce((s, r) => s + (r.spend_criteo || 0), 0);
    const totalRoas = calcRoas(totalRevMtd, totalSpendMtd);
    const roasGoalMtd = roundRoas(originalTotal?.roas?.originalMtd || roasGoal);
    return {
        spend: {
            meta: totalMetaSpend,
            search: totalSearchSpend,
            bing: totalBingSpend,
            criteo: totalCriteoSpend,
            daily: totalSpendMtd,
            mtd: totalSpendMtd,
            originalDaily: totalSpendGoalMtd,
            originalMtd: totalSpendGoalMtd,
            variance: calcVariance(totalSpendMtd, totalSpendGoalMtd),
        },
        revenue: {
            daily: totalRevMtd,
            mtd: totalRevMtd,
            modifier: null,
            originalDaily: totalRevGoalMtd,
            originalMtd: totalRevGoalMtd,
            variance: calcVariance(totalRevMtd, totalRevGoalMtd),
        },
        roas: {
            daily: totalRoas,
            mtd: totalRoas,
            originalMtd: roasGoalMtd,
            variance: calcVariance(totalRoas, roasGoalMtd),
        },
        traffic: {
            meta: originalTotal?.traffic?.meta || 0,
            search: originalTotal?.traffic?.search || 0,
            total: originalTotal?.traffic?.total || 0,
        },
    };
};
// ─── Metric Card Values (now uses formulas from API) ─────────────────────────
export const calcMetricCardValues = (computedTotal, metrics, formulas // ✅ Consumed from API response
) => {
    const { isBudgetBased, roasGoal, totalProjectedRevenue: revGoal, totalSpendGoal, metaSpendPercent, adwordSpendPercent, bingSpendPercent, criteoSpendPercent, } = formulas;
    const calcSpend = (percent) => (totalSpendGoal * percent) / 100;
    const revMtd = computedTotal?.revenue?.mtd || 0;
    const roasMtd = computedTotal?.roas?.mtd || 0;
    const spendMtdActual = computedTotal?.spend?.mtd || 0;
    const revGoalVar = calcVariance(revMtd, revGoal);
    const roasGoalVar = calcVariance(roasMtd, roasGoal);
    const spendGoalVar = calcVariance(spendMtdActual, totalSpendGoal);
    return {
        isBudgetBased,
        roasGoal,
        revGoal,
        totalSpendGoal,
        revMtd,
        roasMtd,
        spendMtdActual,
        revGoalVar,
        roasGoalVar,
        spendGoalVar,
        revGoalVarText: `${Math.abs(Math.round(revGoalVar))}%`,
        roasGoalVarText: `${Math.abs(Math.round(roasGoalVar))}%`,
        spendGoalVarText: `${Math.abs(Math.round(spendGoalVar))}%`,
        revGoalTrend: (revGoalVar >= 0 ? 'up' : 'down'),
        roasGoalTrend: (roasGoalVar >= 0 ? 'up' : 'down'),
        spendGoalTrend: (spendGoalVar >= 0 ? 'up' : 'down'),
        metaSpend: calcSpend(metaSpendPercent),
        adwordSpend: calcSpend(adwordSpendPercent),
        bingSpend: calcSpend(bingSpendPercent),
        criteoSpend: calcSpend(criteoSpendPercent),
    };
};
